import {
  ALL_FACES,
  CARVINGS,
  FINISHES,
  HIDDEN_PATTERNS,
  STAMPS,
  SUPERSTITIONS,
  TABLE_CURIOS,
  TRICKS,
  type AppraisalTarget,
  type CardSpec,
  type FinishId,
  type HiddenPattern,
  type ItemSpec,
  type OmenKind,
  type Square,
} from '../../engine';
import { CampaignError, clone, recruitInto, remove } from '../campaign';
import type { Profile } from '../types';
import {
  APPRAISALS,
  BARGAINS,
  BARGAIN_PAWNS,
  BENCH_CURIOS,
  CARVING_INFO,
  FIXTURE_PRICE,
  LEGENDARIES,
  LONG_NIGHT,
  LOT_KINDS,
  LOT_PRICE,
  LOT_WEIGHT,
  MOURNING_ROUTES,
  PLACEABLE_OMENS,
  SUPERSTITION_INFO,
  lotShape,
  type BargainId,
  type LotKind,
  type LotSize,
  type Rarity,
} from './catalog';
import { isItemUnlocked } from './meta';
import type { LotOffer, NightRun, Offer, OpenLot, PricedOffer, ShopState } from './types';
import {
  basePrice,
  curioSlots,
  ledgerOf,
  mantelSlots,
  mantelUsed,
  nextUid,
  nightRunOf,
  owns,
  pay,
  priceAt,
  rngFor,
  sellValue,
  weightedPick,
} from './util';

/** The Counter (docs/SHOP_DESIGN.md §7): what it stocks, and every deal made at it. */

type Rng = ReturnType<typeof rngFor>;

function pickRarity(rng: Rng, run: NightRun): Rarity {
  const shelf = owns(run, 'wall-shelf') ? 2 : 1;
  return weightedPick<Rarity>(rng, [
    ['common', 70],
    ['uncommon', 25 * shelf],
    ['rare', 5 * shelf],
  ]);
}

function randomFinish(rng: Rng, superstition: boolean): FinishId | undefined {
  if (rng.nextBelow(LONG_NIGHT.FINISH_CHANCE) !== 0) return undefined;
  const list = superstition
    ? FINISHES
    : FINISHES.filter((f) => f !== 'ghost' || rng.nextBelow(3) === 0);
  return list[rng.nextBelow(list.length)];
}

/** A Superstition the run may be offered: unlocked, not owned, not Legendary. */
function superstitionOffer(p: Profile, run: NightRun, rng: Rng, rarity?: Rarity): Offer | null {
  const l = ledgerOf(p);
  const owned = new Set(run.mantel.map((i) => i.id));
  const want = rarity ?? pickRarity(rng, run);
  for (const r of [want, 'common', 'uncommon', 'rare'] as const) {
    const pool = SUPERSTITIONS.filter(
      (id) => SUPERSTITION_INFO[id].rarity === r && !owned.has(id) && isItemUnlocked(l, id),
    );
    if (pool.length === 0) continue;
    const id = pool[rng.nextBelow(pool.length)]!;
    const finish = randomFinish(rng, true);
    return { kind: 'superstition', id, ...(finish ? { finish } : {}) };
  }
  return null;
}

function cardOffer(run: NightRun, rng: Rng, unlockedStamps: boolean): Offer {
  const k = rng.nextBelow(13);
  const base: CardSpec = k === 12 ? { kind: 'wild' } : { kind: 'normal', face: ALL_FACES[k]! };
  const stampOdds = owns(run, 'stamp-rack') ? 2 : 4;
  const stamp =
    unlockedStamps && rng.nextBelow(stampOdds) === 0
      ? STAMPS[rng.nextBelow(STAMPS.length)]
      : undefined;
  const finish = unlockedStamps ? randomFinish(rng, false) : undefined;
  // Tricks (from Night III): one colored card in four is a Trick.
  const trick =
    run.counters.tricksUnlocked === 1 && base.kind === 'normal' && rng.nextBelow(4) === 0
      ? TRICKS[rng.nextBelow(TRICKS.length)]
      : undefined;
  return {
    kind: 'card',
    card: {
      ...base,
      ...(stamp ? { stamp } : {}),
      ...(finish ? { finish } : {}),
      ...(trick ? { trick } : {}),
    },
  };
}

function appraisalOffer(run: NightRun, rng: Rng): Offer {
  const hidden =
    run.night >= 2
      ? APPRAISALS
      : APPRAISALS.filter((t) => !HIDDEN_PATTERNS.includes(t as HiddenPattern));
  return { kind: 'appraisal', id: hidden[rng.nextBelow(hidden.length)]! };
}

function curioOffer(rng: Rng): Offer {
  const all = [...TABLE_CURIOS, ...BENCH_CURIOS];
  return { kind: 'curio', id: all[rng.nextBelow(all.length)]! };
}

function carvingOffer(rng: Rng): Offer {
  const r = rng.nextBelow(100);
  const rarity: Rarity = r < 60 ? 'common' : r < 92 ? 'uncommon' : 'rare';
  const pool = CARVINGS.filter((id) => CARVING_INFO[id].rarity === rarity);
  return { kind: 'carving', id: pool[rng.nextBelow(pool.length)]! };
}

/** One tray item, by the weights of docs/SHOP_DESIGN.md §7.2. */
function trayOffer(p: Profile, run: NightRun, rng: Rng): Offer {
  const l = ledgerOf(p);
  const kind = weightedPick(rng, [
    ['superstition', 60],
    ['curio', l.unlocked.includes('curios') ? 15 : 0],
    ['appraisal', 15],
    ['card', 10],
    ['carving', owns(run, 'carvers-bench') ? 10 : 0],
  ] as const);
  switch (kind) {
    case 'superstition':
      return superstitionOffer(p, run, rng) ?? appraisalOffer(run, rng);
    case 'curio':
      return curioOffer(rng);
    case 'appraisal':
      return appraisalOffer(run, rng);
    case 'card':
      return cardOffer(run, rng, l.unlocked.includes('stamps'));
    case 'carving':
      return carvingOffer(rng);
  }
}

const traySize = (run: NightRun): number =>
  LONG_NIGHT.TRAY_SLOTS + (owns(run, 'glass-case') ? 1 : 0) + (owns(run, 'display-wall') ? 1 : 0);

function stockTray(p: Profile, run: NightRun, key: string, coupon: boolean): PricedOffer[] {
  const rng = rngFor(run, key);
  const out: PricedOffer[] = [];
  for (let i = 0; i < traySize(run); i++) {
    let offer = trayOffer(p, run, rng);
    // Term IV: some Superstitions are offered On Loan.
    if (offer.kind === 'superstition' && run.term >= 4 && rng.nextBelow(4) === 0)
      offer = { ...offer, loan: true };
    const price =
      offer.kind === 'superstition' && offer.loan ? 1 : priceAt(run, basePrice(offer), coupon);
    out.push({ offer, price, sold: false });
  }
  // Guarantee: the first Counter of a run has a Common Superstition the run can afford.
  if (
    run.outcomes.filter((o) => !o.skipped).length <= 1 &&
    !out.some((o) => o.offer.kind === 'superstition')
  ) {
    const offer = superstitionOffer(p, run, rng, 'common');
    if (offer) out[0] = { offer, price: priceAt(run, basePrice(offer), coupon), sold: false };
  }
  return out;
}

function lotKinds(p: Profile, run: NightRun): (readonly [LotKind, number])[] {
  const l = ledgerOf(p);
  const gate: Partial<Record<LotKind, string>> = {
    curio: 'curios',
    carver: 'carvings',
    omen: 'omens',
    mourning: 'carvings',
    estate: 'estate',
  };
  void run;
  return LOT_KINDS.map(
    (k) => [k, gate[k] && !l.unlocked.includes(gate[k]!) ? 0 : LOT_WEIGHT[k]] as const,
  );
}

function stockLots(p: Profile, run: NightRun, key: string, coupon: boolean): LotOffer[] {
  const rng = rngFor(run, key);
  const out: LotOffer[] = [];
  for (let i = 0; i < LONG_NIGHT.LOT_SLOTS; i++) {
    const kind = weightedPick(rng, lotKinds(p, run));
    const size = weightedPick<LotSize>(rng, [
      ['normal', 70],
      ['large', 22],
      ['grand', 8],
    ]);
    out.push({
      kind,
      size,
      price: priceAt(run, LOT_PRICE[size] + (kind === 'estate' ? 2 : 0), coupon),
      sold: false,
    });
  }
  if (run.outcomes.filter((o) => !o.skipped).length <= 1)
    out[0] = {
      kind: 'hearsay',
      size: 'normal',
      price: priceAt(run, LOT_PRICE.normal, coupon),
      sold: false,
    };
  return out;
}

/** What a Lot holds, drawn when it is opened. */
function lotContents(p: Profile, run: NightRun, lot: LotOffer, key: string): Offer[] {
  const rng = rngFor(run, key);
  const { show } = lotShape(lot.kind, lot.size);
  const out: Offer[] = [];
  const l = ledgerOf(p);
  for (let i = 0; i < show * 3 && out.length < show; i++) {
    let offer: Offer | null;
    switch (lot.kind) {
      case 'card':
        offer = cardOffer(run, rng, true);
        break;
      case 'hearsay':
        offer = superstitionOffer(p, run, rng);
        break;
      case 'appraisal':
        offer = appraisalOffer(run, rng);
        break;
      case 'curio':
        offer = curioOffer(rng);
        break;
      case 'carver':
        offer = carvingOffer(rng);
        break;
      case 'mourning': {
        const route = MOURNING_ROUTES[rng.nextBelow(MOURNING_ROUTES.length)]!;
        offer = { kind: 'route', id: route.id, inscription: route.inscription };
        break;
      }
      case 'omen':
        offer = { kind: 'omen', id: PLACEABLE_OMENS[rng.nextBelow(PLACEABLE_OMENS.length)]! };
        break;
      case 'estate': {
        const bargain = weightedPick<BargainId>(
          rng,
          BARGAINS.map((b) => [b, b === 'soul-ticket' ? 1 : 3] as const),
        );
        offer = { kind: 'bargain', id: bargain };
        break;
      }
    }
    if (!offer) continue;
    const key2 = JSON.stringify(offer);
    if (out.some((o) => JSON.stringify(o) === key2)) continue;
    out.push(offer);
  }
  void l;
  return out;
}

/** Opens the Counter after a table (or a Detour): stocks it and applies Favors. */
/**
 * The Rare the next Counter's tray would have held had the last table been won, if it held one:
 * a lost run's summary shows it (§11.21 of the art direction), "It was in the next tray."
 */
export function nextTrayRare(p: Profile): Offer | null {
  const run = structuredClone(nightRunOf(p));
  run.table += 1;
  const visit = `${run.night}:${run.table}:${run.outcomes.length}`;
  const tray = stockTray(p, run, `shop:${visit}:0`, false);
  const rare = tray.find(
    (x) =>
      x.offer.kind === 'superstition' &&
      ['rare', 'legendary'].includes(
        SUPERSTITION_INFO[x.offer.id as keyof typeof SUPERSTITION_INFO]?.rarity ?? '',
      ),
  );
  return rare?.offer ?? null;
}

export function openCounter(p: Profile): void {
  const l = ledgerOf(p);
  const run = nightRunOf(p);
  const visit = `${run.night}:${run.table}:${run.outcomes.length}`;
  const coupon = run.favors.includes('coupon');
  if (coupon) remove(run.favors, 'coupon');
  const tray = stockTray(p, run, `shop:${visit}:0`, coupon);
  if (run.favors.includes('rare-tip')) {
    remove(run.favors, 'rare-tip');
    const offer = superstitionOffer(p, run, rngFor(run, `rare-tip:${visit}`), 'rare');
    if (offer) tray.push({ offer, price: 0, sold: false });
  }
  const backRoom =
    run.night >= 2 && l.unlocked.includes('back-room')
      ? {
          bargain: weightedPick<BargainId>(
            rngFor(run, `back:${visit}`),
            BARGAINS.map((b) => [b, b === 'soul-ticket' ? 1 : 3] as const),
          ),
          sold: false,
        }
      : null;
  const buyRng = rngFor(run, `buyback:${visit}`);
  const buyBack =
    p.collection.length > 0 && buyRng.nextBelow(3) === 0
      ? (p.collection[buyRng.nextBelow(p.collection.length)] ?? null)
      : null;
  const shop: ShopState = {
    tray,
    lots: stockLots(p, run, `lots:${visit}`, coupon),
    backRoom,
    buyBack,
    rings: 0,
    open: null,
    coupon,
  };
  run.shop = shop;
  run.phase = 'counter';
  // Favors that open a free Lot at this Counter.
  if (run.favors.includes('sealed-lot')) {
    remove(run.favors, 'sealed-lot');
    const kind = weightedPick(rngFor(run, `sealed:${visit}`), lotKinds(p, run));
    const lot: LotOffer = { kind, size: 'grand', price: 0, sold: true };
    shop.open = {
      lot,
      contents: lotContents(p, run, lot, `sealed-lot:${visit}`),
      picks: lotShape(kind, 'grand').take,
      free: true,
    };
  } else if (run.favors.includes('carvers')) {
    remove(run.favors, 'carvers');
    const lot: LotOffer = { kind: 'carver', size: 'normal', price: 0, sold: true };
    shop.open = {
      lot,
      contents: lotContents(p, run, lot, `carvers:${visit}`),
      picks: 1,
      free: true,
    };
  }
  for (const o of tray) noteSeen(l, o.offer);
  if (run.brass < 0) l.stats.debts += 1;
}

const noteSeen = (l: ReturnType<typeof ledgerOf>, o: Offer): void => {
  const id = o.kind === 'card' ? null : o.kind === 'appraisal' ? `appraisal:${o.id}` : o.id;
  if (id && !l.seen.includes(id)) l.seen.push(id);
};

function shopOf(run: NightRun): ShopState {
  if (run.phase !== 'counter' || !run.shop) throw new CampaignError('the Counter is not open');
  return run.shop;
}

// ---------------------------------------------------------------------------------------------
// Gaining things
// ---------------------------------------------------------------------------------------------

export interface GainTarget {
  /** The pawn a Carving goes on (or a Bargain names). */
  readonly pawnId?: string;
  readonly pawn2?: string;
  /** The square an Omen is placed on for the next table. */
  readonly square?: Square;
  /** An item a Bargain names (Effigy). */
  readonly uid?: string;
  /** A Carving a Bargain names (Graft). */
  readonly carving?: string;
  /** A card in the run deck (Changeling). */
  readonly deckIndex?: number;
}

const maxCarvings = (run: NightRun): number => (owns(run, 'masters-bench') ? 2 : 1);

/** Adds what an offer gives to the run (bought, picked from a Lot, or a Spoil). */
export function gain(p: Profile, run: NightRun, offer: Offer, target: GainTarget = {}): void {
  const l = ledgerOf(p);
  switch (offer.kind) {
    case 'superstition': {
      if (offer.finish !== 'ghost' && mantelUsed(run) >= mantelSlots(run))
        throw new CampaignError('the Mantel is full: sell a Superstition first');
      let finish = offer.finish;
      if (!finish && run.favors.includes('pearl-tip')) {
        finish = 'pearl';
        remove(run.favors, 'pearl-tip');
      }
      const uid = nextUid(run);
      run.mantel.push({ uid, id: offer.id, ...(finish ? { finish } : {}) });
      if (offer.loan) run.counters[`loan:${uid}`] = 1;
      // Term VII: one Superstition in three rots after 5 tables, one in four is Bolted.
      if (run.term >= 7 && rngFor(run, `rot:${uid}`).nextBelow(3) === 0)
        run.counters[`rotting:${uid}`] = 1;
      if (run.term >= 7 && rngFor(run, `bolt:${uid}`).nextBelow(4) === 0)
        run.counters[`bolted:${uid}`] = 1;
      break;
    }
    case 'curio':
      if (run.curios.length >= curioSlots(run))
        throw new CampaignError('your pockets are full: use or sell a Curio');
      run.curios.push({ uid: nextUid(run, 'c'), id: offer.id });
      break;
    case 'appraisal': {
      const t = offer.id as AppraisalTarget;
      run.appraisals[t] = Math.max(1, run.appraisals[t] ?? 1) + (owns(run, 'assay-office') ? 2 : 1);
      if (
        HIDDEN_PATTERNS.includes(t as HiddenPattern) &&
        !run.discovered.includes(t as HiddenPattern)
      )
        run.discovered.push(t as HiddenPattern);
      break;
    }
    case 'card':
      run.deck.push({ ...offer.card });
      break;
    case 'carving': {
      const id = target.pawnId;
      if (!id || !run.roster.some((r) => r.id === id))
        throw new CampaignError('choose a pawn to carve');
      const list = run.carvings[id] ?? [];
      if (list.some((c) => c.id === offer.id))
        throw new CampaignError('that pawn already has this Carving');
      if (list.length >= maxCarvings(run))
        throw new CampaignError('that pawn has no room for another Carving');
      run.carvings[id] = [...list, { id: offer.id as never }];
      break;
    }
    case 'route':
      run.epitaphs.push({
        id: `M-${nextUid(run)}`,
        owner: 'S',
        sourcePawnId: offer.id,
        inscription: offer.inscription,
      });
      break;
    case 'omen': {
      const sq = target.square;
      if (!sq || sq.y < 1 || sq.y > 4 || sq.x < 0 || sq.x > 5)
        throw new CampaignError('choose a square on ranks 2–5');
      if (run.pendingOmens.some((o) => o.square.x === sq.x && o.square.y === sq.y))
        throw new CampaignError('an Omen already waits on that square');
      run.pendingOmens.push({ kind: offer.id as OmenKind, square: { x: sq.x, y: sq.y } });
      break;
    }
    case 'bargain':
      applyBargain(p, run, offer.id, target);
      break;
  }
  noteSeen(l, offer);
}

// ---------------------------------------------------------------------------------------------
// The tray, the Bell, Lots, the dome
// ---------------------------------------------------------------------------------------------

/** Buys a tray item (a Carving needs a pawn to go on). */
export function buyOffer(profile: Profile, index: number, target: GainTarget = {}): Profile {
  const p = clone(profile);
  const run = nightRunOf(p);
  const shop = shopOf(run);
  const o = shop.tray[index];
  if (!o || o.sold) throw new CampaignError('that is not for sale');
  pay(run, o.price);
  gain(p, run, o.offer, target);
  o.sold = true;
  return p;
}

/** What ringing the bell costs now (docs/SHOP_DESIGN.md §7.3). */
export function ringCost(run: NightRun): number {
  const shop = run.shop;
  const discount = (owns(run, 'shop-bell') ? 2 : 0) + (owns(run, 'silver-bell') ? 2 : 0);
  return Math.max(
    0,
    LONG_NIGHT.RING_COST + (shop?.rings ?? 0) - discount + (run.term >= 5 ? 1 : 0),
  );
}

/** Rings the bell: the tray is restocked. */
export function ringBell(profile: Profile): Profile {
  const p = clone(profile);
  const run = nightRunOf(p);
  const shop = shopOf(run);
  pay(run, ringCost(run));
  shop.rings += 1;
  shop.tray = stockTray(
    p,
    run,
    `shop:${run.night}:${run.table}:${run.outcomes.length}:${shop.rings}`,
    false,
  );
  for (const o of shop.tray) noteSeen(ledgerOf(p), o.offer);
  return p;
}

/** Buys a Lot and opens it. */
export function buyLot(profile: Profile, index: number): Profile {
  const p = clone(profile);
  const run = nightRunOf(p);
  const shop = shopOf(run);
  if (shop.open) throw new CampaignError('finish the open Lot first');
  const lot = shop.lots[index];
  if (!lot || lot.sold) throw new CampaignError('that Lot is gone');
  pay(run, lot.price);
  lot.sold = true;
  const contents = lotContents(
    p,
    run,
    lot,
    `lot:${run.night}:${run.table}:${run.outcomes.length}:${index}`,
  );
  if (lot.kind === 'appraisal' && owns(run, 'appraisers-desk')) {
    const more = appraisalOffer(run, rngFor(run, `lot-extra:${run.night}:${run.table}:${index}`));
    if (!contents.some((c) => JSON.stringify(c) === JSON.stringify(more))) contents.push(more);
  }
  if (lot.kind === 'card' && owns(run, 'engraving-press')) {
    const rng = rngFor(run, `lot-press:${run.night}:${run.table}:${index}`);
    contents.push(cardOffer(run, rng, true), cardOffer(run, rng, true));
  }
  shop.open = { lot, contents, picks: lotShape(lot.kind, lot.size).take, free: false };
  return p;
}

/** Takes one thing from the open Lot. */
export function pickFromLot(profile: Profile, index: number, target: GainTarget = {}): Profile {
  const p = clone(profile);
  const run = nightRunOf(p);
  const shop = shopOf(run);
  const open = shop.open as OpenLot | null;
  if (!open) throw new CampaignError('no Lot is open');
  const offer = open.contents[index];
  if (!offer) throw new CampaignError('nothing there');
  gain(p, run, offer, target);
  open.contents.splice(index, 1);
  open.picks -= 1;
  if (open.picks <= 0 || open.contents.length === 0) shop.open = null;
  return p;
}

/** Closes the open Lot; whatever is left goes back to the Broker. */
export function closeLot(profile: Profile): Profile {
  const p = clone(profile);
  const shop = shopOf(nightRunOf(p));
  shop.open = null;
  return p;
}

/** Buys the Fixture under the dome. */
export function buyFixture(profile: Profile): Profile {
  const p = clone(profile);
  const run = nightRunOf(p);
  shopOf(run);
  const f = run.nightFixture;
  if (!f) throw new CampaignError('the dome is empty');
  pay(run, priceAt(run, FIXTURE_PRICE, run.shop?.coupon));
  run.fixtures.push(f);
  run.nightFixture = null;
  if (f === 'altar-shelf') run.extraSlots += 1;
  return p;
}

// ---------------------------------------------------------------------------------------------
// The Back Room and its Bargains
// ---------------------------------------------------------------------------------------------

function payPawns(p: Profile, run: NightRun, ids: readonly string[], n: number): void {
  if (n === 0) return;
  if (ids.length !== n || new Set(ids).size !== n)
    throw new CampaignError(`pay with ${n} named pawn(s)`);
  for (const id of ids) {
    const entry = run.roster.find((r) => r.id === id);
    if (!entry) throw new CampaignError(`${id} is not on the roster`);
    if (entry.standIn) throw new CampaignError('stand-ins cannot be traded');
  }
  if (run.roster.length - n < 6) throw new CampaignError('keep at least 6 pawns on the roster');
  for (const id of ids) losePawnToBroker(p, run, id);
}

/** A pawn traded or sold: it joins the Broker's collection with its Carvings. */
export function losePawnToBroker(p: Profile, run: NightRun, id: string): void {
  const l = ledgerOf(p);
  run.roster = run.roster.filter((r) => r.id !== id);
  run.fielding = run.fielding.map((x) => (x === id ? '' : x));
  remove(p.stable, id);
  if (!p.collection.includes(id)) p.collection.push(id);
  const carvings = run.carvings[id];
  if (carvings?.length) l.formerCarvings[id] = carvings.map((c) => ({ ...c }));
  delete run.carvings[id];
}

/** What a Bargain does (docs/SHOP_DESIGN.md §10.11). */
export function applyBargain(p: Profile, run: NightRun, id: BargainId, t: GainTarget): void {
  const rng = rngFor(run, `bargain:${id}:${run.uid}`);
  switch (id) {
    case 'soul-ticket': {
      if (mantelUsed(run) >= mantelSlots(run))
        throw new CampaignError('the Mantel needs a free slot');
      const owned = new Set(run.mantel.map((i) => i.id));
      const pool = LEGENDARIES.filter((x) => !owned.has(x));
      if (pool.length === 0) throw new CampaignError('every Legendary is already yours');
      run.mantel.push({ uid: nextUid(run), id: pool[rng.nextBelow(pool.length)]! });
      break;
    }
    case 'effigy': {
      const item = run.mantel.find((i) => i.uid === t.uid);
      if (!item) throw new CampaignError('choose a Superstition to copy');
      // Bolted items can't be destroyed: they stay.
      const bolted = run.mantel.filter(
        (i) => i.uid !== item.uid && run.counters[`bolted:${i.uid}`] === 1,
      );
      run.mantel = [item, { ...item, uid: nextUid(run) }, ...bolted];
      break;
    }
    case 'graft': {
      const from = t.pawnId;
      const to = t.pawn2;
      const carving = (run.carvings[from ?? ''] ?? []).find((c) => c.id === t.carving);
      if (!from || !to || from === to || !carving)
        throw new CampaignError('choose a Carving and the pawn it goes to');
      const list = run.carvings[to] ?? [];
      if (list.length >= maxCarvings(run))
        throw new CampaignError('that pawn has no room for another Carving');
      run.carvings[to] = [...list, { ...carving }];
      run.carvings[from] = (run.carvings[from] ?? []).filter((c) => c.id !== carving.id);
      losePawnToBroker(p, run, from);
      break;
    }
    case 'fusion': {
      const keep = t.pawnId;
      const gone = t.pawn2;
      if (!keep || !gone || keep === gone) throw new CampaignError('choose two pawns to fuse');
      if (run.roster.length - 1 < 6) throw new CampaignError('keep at least 6 pawns on the roster');
      const merged = [...(run.carvings[keep] ?? []), ...(run.carvings[gone] ?? [])];
      run.carvings[keep] = merged.filter((c, i) => merged.findIndex((x) => x.id === c.id) === i);
      losePawnToBroker(p, run, gone);
      break;
    }
    case 'mortgage':
      run.brass += 25;
      run.deck.push({ kind: 'normal', face: { color: 'R', number: 1 }, iou: true });
      run.deck.push({ kind: 'normal', face: { color: 'R', number: 1 }, iou: true });
      break;
    case 'blood-ink': {
      const plain = run.mantel.filter((i) => i.finish !== 'pearl');
      if (plain.length === 0) throw new CampaignError('no Superstition to ink');
      const item = plain[rng.nextBelow(plain.length)]!;
      run.mantel = run.mantel.map((i) => (i.uid === item.uid ? { ...i, finish: 'pearl' } : i));
      break;
    }
    case 'burnt-ledger':
      run.brass = Math.min(0, run.brass);
      for (const target of APPRAISALS) {
        if (
          HIDDEN_PATTERNS.includes(target as HiddenPattern) &&
          !run.discovered.includes(target as HiddenPattern)
        )
          continue;
        run.appraisals[target] = Math.max(1, run.appraisals[target] ?? 1) + 1;
      }
      break;
    case 'changeling': {
      const source = run.deck[t.deckIndex ?? -1];
      if (!source || source.kind !== 'normal') throw new CampaignError('choose a colored card');
      pay(run, 5);
      const others = run.deck
        .map((c, i) => [c, i] as const)
        .filter(([c, i]) => c.kind === 'normal' && i !== t.deckIndex);
      for (const [c, i] of rng.shuffle([...others]).slice(0, 4))
        if (c.kind === 'normal')
          run.deck[i] = { ...c, face: { color: source.face.color, number: c.face.number } };
      break;
    }
    case 'hollow-crown':
      run.counters.hollowCrown = 1;
      break;
    case 'black-wild':
      run.deck.push({ kind: 'wild' }, { kind: 'wild' });
      run.counters.blackWild = 1;
      break;
    case 'exhumation': {
      const id2 = t.pawnId;
      const ins = id2 ? run.bestInscriptions[id2] : undefined;
      if (!id2 || !ins || run.roster.some((r) => r.id === id2))
        throw new CampaignError('choose a pawn lost this run');
      run.epitaphs.push({
        id: `X-${nextUid(run)}`,
        owner: 'S',
        sourcePawnId: id2,
        inscription: ins,
      });
      break;
    }
    case 'brokers-hand':
      run.extraSlots += 1;
      run.anteFactor *= 1.25;
      break;
  }
}

/** Takes the Back Room's Bargain, paying the pawns it costs. */
export function buyBargain(
  profile: Profile,
  payWith: readonly string[],
  target: GainTarget = {},
): Profile {
  const p = clone(profile);
  const run = nightRunOf(p);
  const shop = shopOf(run);
  const room = shop.backRoom;
  if (!room || room.sold) throw new CampaignError('the Back Room is empty');
  // Graft and Fusion take their pawn as part of the deal; the others take pawns as payment.
  const extra =
    room.bargain === 'graft' || room.bargain === 'fusion' ? 0 : BARGAIN_PAWNS[room.bargain];
  for (const id of payWith)
    if (id === target.pawnId || id === target.pawn2)
      throw new CampaignError('pay with another pawn');
  applyBargain(p, run, room.bargain, target);
  payPawns(p, run, payWith, extra);
  room.sold = true;
  return p;
}

// ---------------------------------------------------------------------------------------------
// The Workbench, the shelf and the scale
// ---------------------------------------------------------------------------------------------

export const meltCost = (run: NightRun): number => LONG_NIGHT.MELT_COST + (run.counters.melts ?? 0);

/** Melts a card out of the run deck (its price rises with every card melted). */
export function meltCard(profile: Profile, deckIndex: number): Profile {
  const p = clone(profile);
  const run = nightRunOf(p);
  shopOf(run);
  const card = run.deck[deckIndex];
  if (!card) throw new CampaignError('no such card');
  if (run.deck.length <= 8) throw new CampaignError('the deck cannot go below 8 cards');
  pay(run, priceAt(run, meltCost(run) * (card.iou ? 2 : 1)));
  run.deck.splice(deckIndex, 1);
  run.counters.melts = (run.counters.melts ?? 0) + 1;
  return p;
}

export const hireCost = (run: NightRun): number =>
  LONG_NIGHT.PAWN_HIRE - (owns(run, 'hiring-board') ? 2 : 0);

/** Hires a new named pawn (with a Carving, once the Hiring Board is up). */
export function hirePawn(profile: Profile): Profile {
  const p = clone(profile);
  const run = nightRunOf(p);
  shopOf(run);
  pay(run, priceAt(run, hireCost(run)));
  const pawn = recruitInto(p, 10_000 + run.number);
  run.roster.push({ id: pawn.id, standIn: false });
  if (owns(run, 'hiring-board')) {
    const pool = CARVINGS.filter((c) => CARVING_INFO[c].rarity === 'common');
    run.carvings[pawn.id] = [{ id: pool[rngFor(run, `hire:${pawn.id}`).nextBelow(pool.length)]! }];
  }
  return p;
}

/** Buys back the former pawn the Broker brought to the shelf. */
export function buyBackPawn(profile: Profile): Profile {
  const p = clone(profile);
  const run = nightRunOf(p);
  const shop = shopOf(run);
  const id = shop.buyBack;
  if (!id || !p.collection.includes(id)) throw new CampaignError('nobody is on the shelf');
  pay(run, priceAt(run, LONG_NIGHT.PAWN_BUYBACK));
  remove(p.collection, id);
  p.stable.push(id);
  delete ledgerOf(p).formerCarvings[id];
  const named = p.pawns[id];
  if (named) named.homecomings = (named.homecomings ?? 0) + 1;
  const slot = run.roster.findIndex((r) => r.standIn);
  if (slot >= 0) run.roster[slot] = { id, standIn: false };
  else run.roster.push({ id, standIn: false });
  shop.buyBack = null;
  return p;
}

/** Sells a named pawn to the Broker (it joins his collection and may come back to play). */
export function sellPawn(profile: Profile, pawnId: string): Profile {
  const p = clone(profile);
  const run = nightRunOf(p);
  shopOf(run);
  const entry = run.roster.find((r) => r.id === pawnId);
  if (!entry) throw new CampaignError('that pawn is not on the roster');
  if (entry.standIn) throw new CampaignError('stand-ins cannot be traded');
  if (run.roster.length <= 6) throw new CampaignError('keep at least 6 pawns on the roster');
  losePawnToBroker(p, run, pawnId);
  run.brass += LONG_NIGHT.PAWN_SELL;
  return p;
}

/** What an owned item sells for. */
export function itemSellValue(item: ItemSpec, kind: 'superstition' | 'curio'): number {
  if (kind === 'curio') return sellValue(basePrice({ kind: 'curio', id: item.id }));
  const base = basePrice({
    kind: 'superstition',
    id: item.id,
    ...(item.finish ? { finish: item.finish } : {}),
  });
  return sellValue(base, item.id === 'piggy-bank' ? (item.value ?? 0) : 0);
}

/** Sells a Superstition or a Curio. */
export function sellItem(profile: Profile, uid: string): Profile {
  const p = clone(profile);
  const run = nightRunOf(p);
  shopOf(run);
  const sup = run.mantel.find((i) => i.uid === uid);
  const cur = run.curios.find((i) => i.uid === uid);
  if (sup) {
    if (run.counters[`bolted:${uid}`]) throw new CampaignError('a Bolted item cannot be sold');
    run.mantel = run.mantel.filter((i) => i.uid !== uid);
    if (run.counters[`loan:${uid}`]) delete run.counters[`loan:${uid}`];
    else run.brass += itemSellValue(sup, 'superstition');
  } else if (cur) {
    run.curios = run.curios.filter((i) => i.uid !== uid);
    run.brass += itemSellValue(cur, 'curio');
  } else throw new CampaignError('you do not own that');
  return p;
}

/** Puts the Mantel in a new order (leftmost first). */
export function reorderMantel(profile: Profile, uids: readonly string[]): Profile {
  const p = clone(profile);
  const run = nightRunOf(p);
  if (run.phase === 'playing') throw new CampaignError('the Mantel is reordered between tables');
  const byUid = new Map(run.mantel.map((i) => [i.uid, i]));
  if (
    uids.length !== run.mantel.length ||
    !uids.every((u) => byUid.has(u)) ||
    new Set(uids).size !== uids.length
  )
    throw new CampaignError('list every Superstition once');
  run.mantel = uids.map((u) => byUid.get(u)!);
  return p;
}

// ---------------------------------------------------------------------------------------------
// Bench Curios
// ---------------------------------------------------------------------------------------------

export interface BenchTarget {
  /** Run deck indices the Curio works on. */
  readonly cards?: readonly number[];
  /** Ink Pot: the card whose color the others take. */
  readonly source?: number;
  /** File: raise (true) or lower the number. */
  readonly up?: boolean;
}

/** Uses a Bench Curio between tables (docs/SHOP_DESIGN.md §10.4). */
export function applyBenchCurio(profile: Profile, uid: string, target: BenchTarget = {}): Profile {
  const p = clone(profile);
  const run = nightRunOf(p);
  if (run.phase === 'playing') throw new CampaignError('Bench Curios are used between tables');
  const item = run.curios.find((c) => c.uid === uid);
  if (!item || !(BENCH_CURIOS as readonly string[]).includes(item.id))
    throw new CampaignError('that is not a Bench Curio');
  const cards = [...new Set(target.cards ?? [])];
  for (const i of cards) if (!run.deck[i]) throw new CampaignError('no such card');
  const rng = rngFor(run, `bench:${uid}`);
  switch (item.id) {
    case 'whittling-knife':
      if (cards.length < 1 || cards.length > 2) throw new CampaignError('choose one or two cards');
      if (run.deck.length - cards.length < 8)
        throw new CampaignError('the deck cannot go below 8 cards');
      run.deck = run.deck.filter((_, i) => !cards.includes(i));
      break;
    case 'ink-pot': {
      const src = run.deck[target.source ?? -1];
      if (
        !src ||
        src.kind !== 'normal' ||
        cards.length < 1 ||
        cards.length > 2 ||
        cards.includes(target.source!)
      )
        throw new CampaignError('choose a colored card and up to two others');
      for (const i of cards) {
        const c = run.deck[i]!;
        if (c.kind === 'normal')
          run.deck[i] = { ...c, face: { color: src.face.color, number: c.face.number } };
      }
      break;
    }
    case 'file': {
      const i = cards[0];
      const c = i === undefined ? undefined : run.deck[i];
      if (!c || c.kind !== 'normal' || cards.length !== 1)
        throw new CampaignError('choose one colored card');
      const n = c.face.number + (target.up ? 1 : -1);
      if (n < 1 || n > 3) throw new CampaignError('numbers run from 1 to 3');
      run.deck[i!] = { ...c, face: { color: c.face.color, number: n as 1 | 2 | 3 } };
      break;
    }
    case 'rubber-stamp':
      if (cards.length < 1 || cards.length > 2) throw new CampaignError('choose one or two cards');
      for (const i of cards)
        run.deck[i] = { ...run.deck[i]!, stamp: STAMPS[rng.nextBelow(STAMPS.length)]! };
      break;
    case 'tracing-paper': {
      const i = cards[0];
      if (i === undefined || cards.length !== 1) throw new CampaignError('choose one card to copy');
      run.deck.push({ ...run.deck[i]! });
      break;
    }
    case 'coin-purse':
      run.brass += Math.min(20, Math.max(0, run.brass));
      break;
    case 'appraisers-note': {
      const last = [...run.outcomes].reverse().find((o) => !o.skipped);
      const t =
        (run.counters.lastPatternTarget
          ? APPRAISALS[run.counters.lastPatternTarget - 1]
          : undefined) ?? 'echo';
      void last;
      run.appraisals[t] = Math.max(1, run.appraisals[t] ?? 1) + 1;
      break;
    }
  }
  run.curios = run.curios.filter((c) => c.uid !== uid);
  return p;
}

/** Leaves the Counter for the next table or the next Night. */
export function leaveCounter(profile: Profile, next: (p: Profile) => void): Profile {
  const p = clone(profile);
  const run = nightRunOf(p);
  const shop = shopOf(run);
  if (shop.open) throw new CampaignError('finish the open Lot first');
  run.shop = null;
  next(p);
  return p;
}
